import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import {
  AgentApiError, getAgentApiErrorMessage, getAgentLead, getAgentLeads,
  serializeAgentLead, takeAgentLead, updateAgentLead,
} from '../../modules/b2b/api';
import { AGENT_AUTH_ROUTE } from '../../modules/b2b/auth/config';
import { withAppBasePath } from '../../modules/appMode';
import { fetchB2BLeadsAction, selectB2BLeadAction, takeB2BLeadAction, updateB2BLeadAction } from '../actions/b2bLeads';
import {
  appendLeads, resetPagination, setActiveLead, setActiveLeadId,
  setError, setIsLoading, setIsLoadingActiveLead, setIsUpdating,
  setPagination, setLeads,
} from '../reducers/b2bLeads';
import { activeB2BLeadIdSelector } from '../selectors/b2bLeads';
import { safeApply } from './errors';

const handleUnauthorized = (error: unknown) => {
  if (error instanceof AgentApiError && error.status === 401) {
    window.location.assign(withAppBasePath(AGENT_AUTH_ROUTE));
    return true;
  }
  return false;
};

function* fetchB2BLeads({ payload }: ReturnType<typeof fetchB2BLeadsAction>) {
  const reset = payload?.reset || !payload?.beforeId;
  if (reset) yield put(resetPagination());
  yield put(setIsLoading(true));
  yield put(setError(null));
  try {
    const response: Awaited<ReturnType<typeof getAgentLeads>> = yield call(getAgentLeads, payload);
    const leads = response.leads.map(serializeAgentLead);
    yield put(reset ? setLeads(leads) : appendLeads(leads));
    const lastLead = response.leads[response.leads.length - 1];
    yield put(setPagination({ nextBeforeId: lastLead?.id || null, hasMore: response.leads.length >= (payload?.perPage || 20) }));
  } catch (error) {
    if (!handleUnauthorized(error)) yield put(setError(getAgentApiErrorMessage(error)));
  } finally { yield put(setIsLoading(false)); }
}

function* selectB2BLead({ payload }: ReturnType<typeof selectB2BLeadAction>) {
  yield put(setActiveLeadId(payload.id));
  yield put(setActiveLead(null));
  yield put(setIsLoadingActiveLead(true));
  try {
    const response: Awaited<ReturnType<typeof getAgentLead>> = yield call(getAgentLead, payload.id);
    yield put(setActiveLead(serializeAgentLead(response.lead)));
  } catch (error) {
    if (!handleUnauthorized(error)) yield put(setError(getAgentApiErrorMessage(error)));
  } finally { yield put(setIsLoadingActiveLead(false)); }
}

function* reloadActive(id: string) {
  const activeId: string | null = yield select(activeB2BLeadIdSelector);
  if (activeId !== id) return;
  const response: Awaited<ReturnType<typeof getAgentLead>> = yield call(getAgentLead, id);
  yield put(setActiveLead(serializeAgentLead(response.lead)));
}

function* takeB2BLead({ payload }: ReturnType<typeof takeB2BLeadAction>) {
  yield put(setIsUpdating(true));
  try { yield call(takeAgentLead, payload.id); yield call(reloadActive, payload.id); }
  catch (error) { if (!handleUnauthorized(error)) yield put(setError(getAgentApiErrorMessage(error))); }
  finally { yield put(setIsUpdating(false)); }
}

function* updateB2BLead({ payload }: ReturnType<typeof updateB2BLeadAction>) {
  yield put(setIsUpdating(true));
  try { yield call(updateAgentLead, payload.id, payload.lead); yield call(reloadActive, payload.id); }
  catch (error) { if (!handleUnauthorized(error)) yield put(setError(getAgentApiErrorMessage(error))); }
  finally { yield put(setIsUpdating(false)); }
}

export default function* b2bLeadsSaga() {
  yield all([
    takeLatest(fetchB2BLeadsAction, safeApply(fetchB2BLeads)),
    takeLatest(selectB2BLeadAction, safeApply(selectB2BLead)),
    takeLatest(takeB2BLeadAction, safeApply(takeB2BLead)),
    takeLatest(updateB2BLeadAction, safeApply(updateB2BLead)),
  ]);
}
